/**
 * Skip duplicate files (owner's ask, 2026-10-12).
 *
 * Before a file is uploaded, the uploading account's own earlier uploads are
 * searched for the same file name with the same size. A hit means the file is
 * NOT uploaded again: no bytes, no `src::` record, no indexing dispatch. The
 * file is counted and listed in the batch result as
 * "already uploaded (same name and size)", which is a summary line and not an
 * error.
 *
 * THE RULE, shared with the BunnyQuery widget (the engine implements the same
 * rule; this copy exists because the site runs the published engine package):
 *
 *   1. The project setting `skip_duplicate_files` (the `bq::settings` record in
 *      `__SETTINGS__`, read through the site's project settings store) decides
 *      whether the check runs. ON unless the stored value is the literal
 *      `false`: a project that never opened the setting skips duplicates.
 *   2. `getUniqueId({ unique_id: <file name>, condition: '<=' })` is an
 *      ends-with search over record ids. Indexed files are `src::<storagePath>`,
 *      so the hits are narrowed to ids equal to `src::<name>` (a file at the
 *      root) or ending with `/<name>` (a file in a folder). An id that merely
 *      ends with the same letters (`src::data.pdf` for `a.pdf`) is not a hit,
 *      and neither is a decorated id such as `src::x.pdf|file_summary|window1`.
 *   3. Each candidate record is read (`getRecords` by unique_id). The file is a
 *      duplicate when the record's `data.size_bytes` equals the file's size AND
 *      the record's `user_id` is the signed-in user's id: "within the same
 *      uploading account". Another user's identical file is not a duplicate.
 *   4. A failed lookup (network, no permission, a record written before the
 *      reverse index existed, a hung request) means "not a duplicate". The
 *      check can delay an upload by at most DUPLICATE_LOOKUP_TIMEOUT_MS and can
 *      never block one.
 *
 * DOM-free and import-free on purpose, so it runs under `node --test` as it is
 * and so the two upload paths (the Files page queue in file.ts and the chat
 * attachments in agent.vue) cannot drift from each other.
 */

/** Key of the switch inside the `bq::settings` record's `data`. */
export const SKIP_DUPLICATE_FILES_KEY = "skip_duplicate_files";
/** What an absent key means. */
export const SKIP_DUPLICATE_FILES_DEFAULT = true;

/**
 * Read the switch off a settings record's `data` (or off nothing). Only the
 * literal `false` turns the check off: the record is public and a value this
 * site did not write must not be able to change what the uploader asked for.
 */
export function skipDuplicateFilesFrom(data: { [key: string]: unknown } | null | undefined): boolean {
    if (!data || typeof data !== "object") return SKIP_DUPLICATE_FILES_DEFAULT;
    return data[SKIP_DUPLICATE_FILES_KEY] !== false;
}

/** Code and sentence a skipped duplicate is listed under in a batch result. */
export const DUPLICATE_UPLOAD_CODE = "DUPLICATE";
export const DUPLICATE_UPLOAD_MESSAGE = "already uploaded (same name and size)";

/** Prefix of a file's index record id: `src::<storagePath>`. */
export const SRC_RECORD_PREFIX = "src::";

/** Most ids one ends-with lookup fetches. */
export const DUPLICATE_LOOKUP_LIMIT = 500;
/** Most candidate records read per file. Past this the rest are left unread (not duplicates). */
export const DUPLICATE_CANDIDATE_READS = 20;
/** Longest the whole check may hold an upload. */
export const DUPLICATE_LOOKUP_TIMEOUT_MS = 15_000;

/** The last segment of a storage path: `docs/2024/report.pdf` is `report.pdf`. */
export function fileNameOf(path: string): string {
    const text = String(path || "");
    const cut = text.lastIndexOf("/");
    return cut === -1 ? text : text.slice(cut + 1);
}

/** The storage path a `src::` id was minted from ("" for an id that is not one). */
export function storagePathOfSrcId(id: string): string {
    const text = String(id || "");
    return text.startsWith(SRC_RECORD_PREFIX) ? text.slice(SRC_RECORD_PREFIX.length) : "";
}

/**
 * Whether an id names a file called `name`: `src::<name>` at the root, or
 * `src::<folder>/<name>` in a folder. Nothing else.
 */
export function isSrcIdForName(id: string, name: string): boolean {
    const text = String(id || "");
    if (!name || !text.startsWith(SRC_RECORD_PREFIX)) return false;
    return text === SRC_RECORD_PREFIX + name || text.endsWith("/" + name);
}

/** The ids of an ends-with lookup that actually name the file, in the order returned. */
export function srcIdCandidatesForName(ids: Array<string | null | undefined>, name: string): string[] {
    const out: string[] = [];
    for (const id of ids) {
        const text = String(id || "");
        if (text && isSrcIdForName(text, name) && !out.includes(text)) out.push(text);
    }
    return out;
}

/**
 * Whether a candidate record is this account's copy of a file of this size.
 * `size_bytes` is what the dashboard writes on the `src::` record at upload
 * (ensureFileIndexRecord); a record without it can never match.
 */
export function isDuplicateRecord(rec: any, size: number, userId: string): boolean {
    if (!rec || typeof rec !== "object" || !userId) return false;
    if (rec.user_id !== userId) return false;
    const stored = rec?.data?.size_bytes;
    if (typeof stored !== "number" || !Number.isFinite(stored)) return false;
    return stored === size;
}

/** The two calls the check makes. Any object with them will do (a skapi instance, or a test double). */
export type DuplicateLookupClient = {
    getUniqueId: (params: any, fetchOptions?: any) => Promise<any>;
    getRecords: (params: any, fetchOptions?: any) => Promise<any>;
};

/** A stored file that makes the upload a duplicate. */
export type DuplicateMatch = {
    /** Where the stored copy is, relative to the project's file storage root. */
    storagePath: string;
    /** Its `src::` record id. */
    uniqueId: string;
    /** Its record id, when the lookup returned one. */
    recordId: string;
    /** The stored size, which equals the file's. */
    size: number;
};

export type FindDuplicateArgs = {
    /** The signed-in skapi instance. Missing or without the two calls: not a duplicate. */
    client: DuplicateLookupClient | null | undefined;
    /** The project whose files are searched. */
    service: string;
    /** The signed-in user's id. Missing: not a duplicate (nothing could match). */
    userId: string | null | undefined;
    /** The file's name (its last path segment is used). */
    name: string;
    /** The file's size in bytes. */
    size: number;
    /** The project's `bq::settings` data, or null when it has none. Decides whether the check runs. */
    settings?: { [key: string]: unknown } | null;
    /** Overrides `settings` when given: true runs the check, false does not. */
    enabled?: boolean;
    /** Test hook. Defaults to DUPLICATE_LOOKUP_TIMEOUT_MS. */
    timeoutMs?: number;
};

/**
 * Find this account's stored copy of a file, or null. NEVER rejects and never
 * takes longer than the timeout: every failure answers "not a duplicate".
 */
export async function findDuplicateUpload(args: FindDuplicateArgs): Promise<DuplicateMatch | null> {
    const enabled = typeof args.enabled === "boolean" ? args.enabled : skipDuplicateFilesFrom(args.settings);
    if (!enabled) return null;
    const client = args.client;
    if (!client || typeof client.getUniqueId !== "function" || typeof client.getRecords !== "function") return null;
    const service = String(args.service || "");
    const userId = String(args.userId || "");
    const name = normalizeName(fileNameOf(args.name));
    const size = args.size;
    if (!service || !userId || !name) return null;
    if (typeof size !== "number" || !Number.isFinite(size) || size < 0) return null;

    const timeoutMs = typeof args.timeoutMs === "number" && args.timeoutMs >= 0 ? args.timeoutMs : DUPLICATE_LOOKUP_TIMEOUT_MS;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), timeoutMs);
    });
    try {
        return await Promise.race([lookup(client, service, userId, name, size), deadline]);
    } catch {
        return null;
    } finally {
        if (timer !== undefined) clearTimeout(timer);
    }
}

async function lookup(
    client: DuplicateLookupClient,
    service: string,
    userId: string,
    name: string,
    size: number,
): Promise<DuplicateMatch | null> {
    // Ends-with over record ids. A throw here (no permission, a transport
    // failure) is caught by the caller and means "not a duplicate".
    const found: any = await client.getUniqueId(
        { service, unique_id: name, condition: "<=" },
        { fetchMore: false, limit: DUPLICATE_LOOKUP_LIMIT },
    );
    const rows: any[] = Array.isArray(found?.list) ? found.list : [];
    const recordIdOf = new Map<string, string>();
    for (const row of rows) {
        const id = String(row?.unique_id || "");
        if (id && !recordIdOf.has(id)) recordIdOf.set(id, String(row?.record_id || ""));
    }
    const candidates = srcIdCandidatesForName([...recordIdOf.keys()], name).slice(0, DUPLICATE_CANDIDATE_READS);
    for (const uniqueId of candidates) {
        let rec: any = null;
        try {
            const read: any = await client.getRecords({ service, unique_id: uniqueId });
            rec = Array.isArray(read?.list) ? read.list[0] : null;
        } catch {
            // A record that vanished between the two calls (NOT_EXISTS), or a
            // read refused: this candidate is unknown, so it is not a duplicate.
            continue;
        }
        if (isDuplicateRecord(rec, size, userId)) {
            return {
                storagePath: storagePathOfSrcId(uniqueId),
                uniqueId,
                recordId: String(rec?.record_id || recordIdOf.get(uniqueId) || ""),
                size,
            };
        }
    }
    return null;
}

/** Storage paths are written NFC (normalizeStoragePath); the lookup name must match them. */
function normalizeName(name: string): string {
    const text = String(name || "");
    try {
        return text.normalize("NFC");
    } catch {
        return text;
    }
}
